import { customElement, observable, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { route, IRouter, type Params } from '@aurelia/router';
import { I18N, Signals } from '@aurelia/i18n';
import template from './collections-view.html';
import {
  addMediaItemsToCollection,
  completeImportProtocol,
  createImportProtocol,
  deleteCollection,
  foldMediaItems,
  formatFileSize,
  getCollection,
  getCollectionExportDownloadUrl,
  getCollectionExportStatus,
  getCollectionImportMediaUrl,
  getCollectionImportStatus,
  getMediaPreviewUrl,
  listRootCollections,
  moveMediaItemsBetweenCollections,
  moveCollection,
  removeMediaItemsFromCollection,
  searchCollections,
  startCollectionExport,
  startCollectionImport,
  bulkUpdateCapturedAt,
  transferMediaOwnership,
  updateCollection,
  updateMediaItem,
  uploadCollectionImport,
  uploadMediaFile,
} from '../services/api.service';
import type {
  Collection,
  CollectionDetail,
  CollectionRef,
  CollectionSearchResult,
  CollectionExportStatus,
  CollectionImportInspection,
  CollectionImportStatus,
  ImportedPersonCandidate,
  ImportedMarkerSample,
  ImportedPersonInspection,
  MediaItem,
  MediaVisibility,
  Person,
  TagRef,
} from '../services/api.types';
import type { AppUser } from '../services/api.types';
import { MediaType, type MediaFoldGroup } from '../services/api.types';
import type { FoldChangeOperation } from '../components/media-lightbox';
import type { UploadProgressItem } from '../components/media-upload-progress';
import { ConfirmDialog } from '../components/confirm-dialog';
import { hasPermission, PERMISSIONS_CHANGED_CHANNEL } from '../services/permissions.service';
import {
  faceCropStyle,
  facePreviewContainerStyle,
  getFaceCropRect,
  type FaceRect,
} from '../services/face-crop.util';
import { getFaceZoomThresholdPx } from '../services/face-detection-settings.service';

const MAX_VISIBLE_TILE_TAGS = 3;
const SELECT_ALL_CONFIRM_THRESHOLD = 100;
const TOUCH_TAP_MAX_DELAY_MS = 450;
const TOUCH_TAP_MAX_MOVEMENT_PX = 18;
type SelectionAction = 'collection' | 'move' | 'family' | 'private' | 'remove' | 'fold' | 'date' | 'chronology' | 'transfer';

interface CollectionScrollAnchor {
  tileKey: string | null;
  fallbackIndex: number;
  offsetTop: number;
  scrollTop: number;
  focusedTileKey: string | null;
  hadTileFocus: boolean;
}

type UnindexedGridTile =
  | { kind: 'collection'; collection: Collection }
  | { kind: 'media'; item: MediaItem; mediaIndex: number };

type GridTile =
  | { kind: 'collection'; collection: Collection; tileIndex: number }
  | { kind: 'media'; item: MediaItem; mediaIndex: number; tileIndex: number };

type GridEntry = GridTile | { kind: 'year'; year: number | null };

// Same sort options/persistence pattern as gallery-view.ts's `sortOption`, scoped to this view.
const SORT_STORAGE_KEY = 'radish_collections_sort';
const FOLDED_FILTER_STORAGE_KEY = 'radish_collections_only_folded';
const FILTERBAR_STORAGE_KEY = 'radish_collections_filter_bar';
const COLLECTION_SEARCH_STORAGE_KEY = 'radish_collections_collection_search';
const COLLECTION_SEARCH_QUERY_KEY = 'collectionSearch';
const COLLECTION_SEARCH_RETURN_STORAGE_KEY = 'radish_collections_search_return';
const COLLECTION_SEARCH_SCROLL_QUERY_STORAGE_KEY = 'radish_collections_search_scroll_query';
const COLLECTION_SEARCH_SCROLL_TOP_STORAGE_KEY = 'radish_collections_search_scroll_top';
const MEDIA_SEARCH_STORAGE_KEY = 'radish_collections_media_search';
const TAGS_OVERLAY_STORAGE_KEY = 'radish_media_show_tags_overlay';
const TIME_OVERLAY_STORAGE_KEY = 'radish_media_show_time_overlay';
const FILENAME_OVERLAY_STORAGE_KEY = 'radish_media_show_filename_overlay';
const SEARCH_DEBOUNCE_MS = 400;

@route({ path: ['collections', 'collections/:id'], title: 'Collections' })
@customElement({ name: 'collections-view', template })
export class CollectionsView {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private readonly router: IRouter = resolve(IRouter);
  private _localeSub: IDisposable | null = null;
  private _permissionsSub: IDisposable | null = null;

  public _locale = '';

  public collectionId: string | null = null;
  public detail: CollectionDetail | null = null;
  public rootCollections: Collection[] = [];
  public collectionSearchResults: CollectionSearchResult[] = [];
  public collectionSearchTotalCount = 0;
  public collectionSearchPaths: Record<string, string> = {};
  public isLoading = false;
  public isRefreshing = false;
  public error = '';
  public showTags = localStorage.getItem(TAGS_OVERLAY_STORAGE_KEY) === 'true';
  public showTime = localStorage.getItem(TIME_OVERLAY_STORAGE_KEY) === 'true';
  public showFileName = localStorage.getItem(FILENAME_OVERLAY_STORAGE_KEY) === 'true';
  public showCollectionFilterBar = localStorage.getItem(FILTERBAR_STORAGE_KEY) === 'true';
  @observable public onlyFolded = localStorage.getItem(FOLDED_FILTER_STORAGE_KEY) === 'true';
  @observable public collectionSearchText = localStorage.getItem(COLLECTION_SEARCH_STORAGE_KEY) ?? '';
  @observable public mediaSearchText = localStorage.getItem(MEDIA_SEARCH_STORAGE_KEY) ?? '';
  private _searchDebounceHandle: number | null = null;
  private _suppressFilterReload = false;
  private _pendingCollectionSearchText: string | null = null;
  private _pendingCollectionSearchScrollTop: number | null = null;

  // One "sortBy:direction" order drives both media items and subcollections in this view.
  @observable public sortOption = localStorage.getItem(SORT_STORAGE_KEY) ?? 'capturedat:desc';
  private _ready = false;

  // ── Infinite scroll (media items within a collection) ─────────────────────
  public page = 1;
  public pageSize = 24;
  public isLoadingMore = false;
  public scrollContainer: HTMLElement | null = null;
  public scrollSentinel: HTMLElement | null = null;
  public visibleMediaStart = 0;
  public visibleMediaEnd = 0;
  private _scrollObserver: IntersectionObserver | null = null;
  private _visibleRangeResizeObserver: ResizeObserver | null = null;
  private _visibleRangeFrame: number | null = null;
  private readonly _visibleRangeScrollHandler = (): void => this.scheduleVisibleMediaRangeUpdate();
  private readonly _visibleRangeResizeHandler = (): void => this.scheduleVisibleMediaRangeUpdate();
  private _reloadVersion = 0;

  // Upload / drop state (only enabled when viewing a specific collection)
  public dragOverlayVisible = false;
  private dragDepth = 0;
  public uploadItems: UploadProgressItem[] = [];
  public uploadModalVisible = false;
  public uploadStartedAt: number | null = null;

  // Lightbox state
  public lightboxItems: MediaItem[] = [];
  public lightboxStartIndex = 0;
  public focusedIndex = 0;
  public galleryGridEl: HTMLElement | null = null;
  public tileHasFocus = false;
  private _touchTapStart: { index: number; x: number; y: number } | null = null;
  private _lastTouchTap: { index: number; at: number } | null = null;
  private _touchDoubleTapHandledAt = 0;
  private _penTapHandledAt = 0;

  // Selection
  public selectMode = false;
  public selectedIds: Record<string, boolean> = {};
  public selectedCollectionIds: Record<string, boolean> = {};
  private _lastSelectedIdx = -1;
  public rangeSelectionMode = false;
  public isSelectingAll = false;
  public selectAllProgress = 0;
  public selectAllTotal = 0;
  public showFoldModal = false;
  public foldItems: MediaItem[] = [];
  public folding = false;
  public movingSelection = false;
  public settingVisibility = false;
  public showBulkDateModal = false;
  public bulkDateUpdating = false;
  public showChronologyOrderModal = false;
  public chronologyOrderItems: MediaItem[] = [];
  public chronologyOrderUpdating = false;
  public showOwnerPicker = false;
  public hasMaintenanceAccess = hasPermission('maintenance.access');
  public transferring = false;
  public selectionActionsOpen = false;
  public selectionActionsHost: HTMLElement | null = null;
  public titlebarOverflowOpen = false;
  public titlebarOverflowHost: HTMLElement | null = null;
  private readonly _selectionDocumentHandler = (event: MouseEvent): void => {
    const target = event.target;
    if (target instanceof Node && !this.selectionActionsHost?.contains(target))
      this.selectionActionsOpen = false;
  };
  private readonly _titlebarOverflowDocumentHandler = (event: MouseEvent): void => {
    const target = event.target;
    if (target instanceof Node && !this.titlebarOverflowHost?.contains(target))
      this.titlebarOverflowOpen = false;
  };

  // Modals
  public showCollectionModal = false;
  public collectionToEdit: Collection | null = null;
  public openingCollectionModal = false;
  public showPicker = false;
  public locationMapCollection: Collection | null = null;
  public showTreeModal = false;
  public showExportModal = false;
  public exportExcludeVideos = true;
  public exportStarting = false;
  public exportJobId: string | null = null;
  public exportStatus: CollectionExportStatus | null = null;
  public exportError = '';
  private _exportPollVersion = 0;
  public showImportModal = false;
  public importFileInput: HTMLInputElement | null = null;
  public importParentCollectionId: string | null = null;
  public importInspection: CollectionImportInspection | null = null;
  public importMappings: Record<string, string> = {};
  // Per-person combobox option lists (same-name candidates plus any candidate picked via the
  // search modal). Kept as a plain record bound directly in the template (`importPersonOptions
  // [person.id]`) rather than computed inside a template call expression — Aurelia only
  // reconnects observers for properties it can see in the bound AST, so a call like
  // `importCandidatesForPerson(person)` never re-evaluates when unrelated internal state (like
  // the search-selected candidate) changes; only re-assigning this record's root reference does.
  public importPersonOptions: Record<string, ImportedPersonCandidate[]> = {};
  public showImportPersonSearch = false;
  public importPersonSearchPersonId: string | null = null;
  private importSelectedCandidates: Record<string, ImportedPersonCandidate> = {};
  public importUploading = false;
  public importUploadProgress = 0;
  public importStarting = false;
  public importStatus: CollectionImportStatus | null = null;
  public importError = '';
  private _importPollVersion = 0;
  private _pickerContext: 'lightbox' | 'selection' | 'moveSelection' | null = null;
  private _pickerLightboxItemId: string | null = null;
  public pickerMode: 'add' | 'move' = 'add';

  public getMediaPreviewUrl = getMediaPreviewUrl;

  public get isCollectionSearchMode(): boolean { return !!this.collectionSearchText.trim(); }

  public get isRootView(): boolean {
    return this.collectionId === null || this.isCollectionSearchMode;
  }

  public get currentCollection(): Collection | null { return this.detail?.collection ?? null; }

  private routeCollectionSearchText(): string {
    if (this.collectionId !== null) return '';
    return new URLSearchParams(window.location.search).get(COLLECTION_SEARCH_QUERY_KEY)?.trim() ?? '';
  }

  private updateCollectionSearchUrl(searchText: string): void {
    if (this._suppressFilterReload) return;
    const path = this.collectionId === null
      ? 'collections'
      : `collections/${this.collectionId}`;
    void this.router.load(path, {
      historyStrategy: 'replace',
      transitionPlan: 'none',
      queryParams: searchText
        ? { [COLLECTION_SEARCH_QUERY_KEY]: searchText }
        : {},
    });
  }

  private rememberCollectionSearchForReturn(searchText: string): void {
    const normalizedSearchText = searchText.trim();
    if (!normalizedSearchText) return;
    this._pendingCollectionSearchText = normalizedSearchText;
    sessionStorage.setItem(COLLECTION_SEARCH_RETURN_STORAGE_KEY, normalizedSearchText);
    sessionStorage.setItem(COLLECTION_SEARCH_SCROLL_QUERY_STORAGE_KEY, normalizedSearchText);
    sessionStorage.setItem(
      COLLECTION_SEARCH_SCROLL_TOP_STORAGE_KEY,
      String(Math.max(0, this.scrollContainer?.scrollTop ?? 0)),
    );
  }

  private pendingCollectionSearchForReturn(): string {
    return this._pendingCollectionSearchText
      ?? sessionStorage.getItem(COLLECTION_SEARCH_RETURN_STORAGE_KEY)?.trim()
      ?? '';
  }

  private clearPendingCollectionSearchForReturn(): void {
    this._pendingCollectionSearchText = null;
    this._pendingCollectionSearchScrollTop = null;
    sessionStorage.removeItem(COLLECTION_SEARCH_RETURN_STORAGE_KEY);
    sessionStorage.removeItem(COLLECTION_SEARCH_SCROLL_QUERY_STORAGE_KEY);
    sessionStorage.removeItem(COLLECTION_SEARCH_SCROLL_TOP_STORAGE_KEY);
  }

  private consumeCollectionSearchScrollTop(searchText: string): number | null {
    const storedSearchText = sessionStorage.getItem(COLLECTION_SEARCH_SCROLL_QUERY_STORAGE_KEY);
    const storedScrollTop = sessionStorage.getItem(COLLECTION_SEARCH_SCROLL_TOP_STORAGE_KEY);
    sessionStorage.removeItem(COLLECTION_SEARCH_SCROLL_QUERY_STORAGE_KEY);
    sessionStorage.removeItem(COLLECTION_SEARCH_SCROLL_TOP_STORAGE_KEY);
    if (storedSearchText !== searchText || storedScrollTop === null) return null;

    const scrollTop = Number(storedScrollTop);
    return Number.isFinite(scrollTop) && scrollTop >= 0 ? scrollTop : null;
  }

  public get createCollectionActionKey(): string {
    return this.isRootView ? 'collections.newCollection' : 'collections.newSubCollection';
  }

  public get exportInProgress(): boolean {
    return this.exportStarting
      || this.exportStatus?.state === 'preparing'
      || this.exportStatus?.state === 'creating';
  }

  public get importInProgress(): boolean {
    return this.importUploading
      || this.importStarting
      || this.importStatus?.state === 'importing';
  }

  public get importProgressPercent(): number {
    const status = this.importStatus;
    if (!status) return 0;
    const stages = [
      [status.collectionsProcessed, status.collectionsTotal],
      [status.mediaItemsProcessed, status.mediaItemsTotal],
      [status.markersProcessed, status.markersTotal],
    ] as const;
    const active = stages.find(([processed, total]) => total > processed)
      ?? stages.find(([, total]) => total > 0);
    return active ? Math.round((active[0] / active[1]) * 100) : 0;
  }

  public get importProgressMessage(): string {
    const status = this.importStatus;
    if (!status) return '';
    if (status.collectionsProcessed < status.collectionsTotal)
      return this.i18n.tr('collections.import.collectionsProgress', {
        processed: status.collectionsProcessed,
        total: status.collectionsTotal,
      });
    if (status.mediaItemsProcessed < status.mediaItemsTotal)
      return this.i18n.tr('collections.import.mediaProgress', {
        processed: status.mediaItemsProcessed,
        total: status.mediaItemsTotal,
      });
    return this.i18n.tr('collections.import.markersProgress', {
      processed: status.markersProcessed,
      total: status.markersTotal,
    });
  }

  public get importAutomaticMappingCount(): number {
    return Object.keys(this.importInspection?.knownMappings ?? {}).length;
  }

  public get exportDownloadUrl(): string {
    return this.exportJobId ? getCollectionExportDownloadUrl(this.exportJobId) : '';
  }

  public get exportPreparationPercent(): number {
    if (this.exportStatus?.state !== 'preparing' || this.exportStatus.filesTotal === 0)
      return 0;
    return Math.round((this.exportStatus.filesProcessed / this.exportStatus.filesTotal) * 100);
  }

  public get mediaItems(): MediaItem[] { return this.detail?.mediaItems ?? []; }

  public get childCollections(): Collection[] { return this.detail?.childCollections ?? []; }

  public get mediaItemsTotalCount(): number { return this.detail?.mediaItemsTotalCount ?? 0; }

  public get hasMore(): boolean {
    return !this.isRootView && this.mediaItems.length < this.mediaItemsTotalCount;
  }

  public get hasSelection(): boolean { return this.selectionCount > 0; }

  public get hasCollectionFilters(): boolean {
    return !!this.collectionSearchText.trim()
      || !!this.mediaSearchText.trim()
      || (!this.isRootView && this.onlyFolded);
  }

  public get hasCollectionSearch(): boolean {
    return !!this.collectionSearchText.trim() || !!this.mediaSearchText.trim();
  }

  public get visibleCollections(): Collection[] {
    const collections = this.isCollectionSearchMode
      ? this.collectionSearchResults.map(result => result.collection)
      : (this.collectionId === null ? this.rootCollections : this.childCollections);
    const [sortBy, direction] = this.sortOption.split(':');
    const descending = direction === 'desc';

    return [...collections].sort((left, right) => {
      if (sortBy === 'capturedat') {
        const leftStartDate = this.collectionSortDate(left, sortBy);
        const rightStartDate = this.collectionSortDate(right, sortBy);

        // Collections without a user-defined start date stay at the top in either direction.
        if (leftStartDate === null || rightStartDate === null) {
          if (leftStartDate === null && rightStartDate !== null) return -1;
          if (leftStartDate !== null && rightStartDate === null) return 1;

          const leftCreatedDate = this.collectionSortDate(left, 'uploadedat');
          const rightCreatedDate = this.collectionSortDate(right, 'uploadedat');
          if (leftCreatedDate !== null && rightCreatedDate !== null
            && leftCreatedDate !== rightCreatedDate) {
            return descending
              ? rightCreatedDate - leftCreatedDate
              : leftCreatedDate - rightCreatedDate;
          }
        } else if (leftStartDate !== rightStartDate) {
          return descending ? rightStartDate - leftStartDate : leftStartDate - rightStartDate;
        }
      } else if (sortBy === 'uploadedat') {
        const leftDate = this.collectionSortDate(left, sortBy);
        const rightDate = this.collectionSortDate(right, sortBy);
        if (leftDate !== null && rightDate !== null && leftDate !== rightDate) {
          return descending ? rightDate - leftDate : leftDate - rightDate;
        }
      }

      const titleOrder = left.title.localeCompare(right.title, this._locale || undefined, {
        sensitivity: 'base',
      });
      return titleOrder || left.id.localeCompare(right.id);
    });
  }

  public get isDateSort(): boolean {
    return this.sortOption.startsWith('capturedat:');
  }

  public get gridTiles(): GridTile[] {
    const unindexedTiles: UnindexedGridTile[] = [
      ...this.visibleCollections.map(collection => ({ kind: 'collection' as const, collection })),
      ...(this.isRootView
        ? []
        : this.mediaItems.map((item, mediaIndex) => ({ kind: 'media' as const, item, mediaIndex }))),
    ];

    if (this.isDateSort) {
      const descending = this.sortOption.endsWith(':desc');
      unindexedTiles.sort((left, right) => this.compareGridTiles(left, right, descending));
    }

    return unindexedTiles.map((tile, tileIndex) => ({ ...tile, tileIndex }));
  }

  public get gridEntries(): GridEntry[] {
    const tiles = this.gridTiles;
    if (!this.isDateSort || this.isCollectionSearchMode || tiles.length === 0) return tiles;

    const entries: GridEntry[] = [];
    let currentYear: number | null | undefined;
    for (const tile of tiles) {
      const year = this.gridTileYear(tile);
      if (year !== currentYear) {
        entries.push({ kind: 'year', year });
        currentYear = year;
      }
      entries.push(tile);
    }
    return entries;
  }

  private compareGridTiles(
    left: UnindexedGridTile,
    right: UnindexedGridTile,
    descending: boolean,
  ): number {
    const leftDate = this.gridTileDate(left);
    const rightDate = this.gridTileDate(right);
    if (leftDate === null || rightDate === null) {
      if (leftDate === null && rightDate !== null) return -1;
      if (leftDate !== null && rightDate === null) return 1;
      return 0;
    } else if (leftDate !== rightDate) {
      return descending ? rightDate - leftDate : leftDate - rightDate;
    }

    const leftTitle = left.kind === 'collection' ? left.collection.title : left.item.title;
    const rightTitle = right.kind === 'collection' ? right.collection.title : right.item.title;
    const titleOrder = leftTitle.localeCompare(rightTitle, this._locale || undefined, {
      sensitivity: 'base',
    });
    if (titleOrder !== 0) return titleOrder;

    const leftId = left.kind === 'collection' ? left.collection.id : left.item.id;
    const rightId = right.kind === 'collection' ? right.collection.id : right.item.id;
    return leftId.localeCompare(rightId);
  }

  private gridTileDate(tile: UnindexedGridTile | GridTile): number | null {
    const value = tile.kind === 'collection'
      ? tile.collection.collectionDate
      : tile.item.capturedAt;
    if (!value) return null;
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private gridTileYear(tile: GridTile): number | null {
    const value = tile.kind === 'collection'
      ? tile.collection.collectionDate
      : tile.item.capturedAt;
    const yearMatch = value?.match(/^(\d{4})/);
    if (yearMatch) return Number(yearMatch[1]);

    const timestamp = this.gridTileDate(tile);
    return timestamp === null ? null : new Date(timestamp).getFullYear();
  }

  private collectionSortDate(collection: Collection, sortBy: string): number | null {
    const date = sortBy === 'capturedat'
      ? collection.collectionDate
      : collection.createdAt;
    if (!date) return null;
    const timestamp = Date.parse(date);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  public get selectedCollectionItems(): Collection[] {
    return this.visibleCollections.filter(collection => this.selectedCollectionIds[collection.id]);
  }

  public get selectedMediaIds(): string[] {
    return Object.keys(this.selectedIds).filter(id => this.selectedIds[id]);
  }

  public get hasSelectedMedia(): boolean { return this.selectedMediaIds.length > 0; }

  private get canApplyMediaSelectionAction(): boolean {
    return this.hasSelectedMedia
      && this.selectedCollectionItems.length === 0
      && this.selectedMediaItems.length === this.selectedMediaIds.length;
  }

  public get canAddSelectionToCollection(): boolean {
    return this.canApplyMediaSelectionAction;
  }

  public get canMoveSelection(): boolean {
    return !this.isRootView
      && this.canApplyMediaSelectionAction;
  }

  public get selectionCount(): number {
    return this.selectedMediaIds.length
      + Object.values(this.selectedCollectionIds).filter(Boolean).length;
  }

  public get selectedMediaItems(): MediaItem[] {
    return this.mediaItems.filter(item => this.selectedIds[item.id]);
  }

  public get canSelectRange(): boolean {
    return this.hasSelection && this._lastSelectedIdx >= 0 && this._lastSelectedIdx < this.galleryTileCount;
  }

  public get canRemoveSelection(): boolean {
    return !this.isRootView && this.hasSelection;
  }

  public get canFoldSelection(): boolean {
    return !this.isRootView
      && this.canApplyMediaSelectionAction
      && this.selectedMediaItems.length >= 2
      && this.selectedMediaItems.every(item => item.mediaType === MediaType.Image);
  }

  public get canSetSelectedDate(): boolean {
    return !this.isRootView
      && this.canApplyMediaSelectionAction
      && this.selectedMediaItems.every(item => !item.capturedAt || !item.isCapturedAtTimeAccurate);
  }

  public get canOrderSelectionChronologically(): boolean {
    if (this.isRootView || !this.canApplyMediaSelectionAction) return false;
    const items = this.selectedMediaItems;
    if (items.length < 2) return false;
    const firstDate = items[0].capturedAt ? this.localDateKey(new Date(items[0].capturedAt)) : null;
    return firstDate !== null
      && items.every(item =>
        !!item.capturedAt
        && !item.isCapturedAtTimeAccurate
        && this.localDateKey(new Date(item.capturedAt)) === firstDate);
  }

  public get canTransferSelection(): boolean {
    return this.hasMaintenanceAccess && this.canApplyMediaSelectionAction;
  }

  public get breadcrumb(): CollectionRef[] {
    return this.detail?.breadcrumb ?? [];
  }

  public binding(): void {
    this._locale = this.i18n.getLocale();
    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this._locale = this.i18n.getLocale();
    });
    this._permissionsSub = this.ea.subscribe(PERMISSIONS_CHANGED_CHANNEL, () => {
      this.hasMaintenanceAccess = hasPermission('maintenance.access');
    });
  }

  public async loading(params: Params): Promise<void> {
    this.selectMode = false;
    this.clearSelection();
    this.collectionId = params.id ?? null;
    const routeSearchText = this.routeCollectionSearchText();
    const searchText = routeSearchText
      || (this.collectionId === null ? this.pendingCollectionSearchForReturn() : '')
      || (this.collectionId === null ? localStorage.getItem(COLLECTION_SEARCH_STORAGE_KEY)?.trim() ?? '' : '');
    const previousSuppressFilterReload = this._suppressFilterReload;
    this._suppressFilterReload = true;
    this.collectionSearchText = searchText;
    if (searchText && this.mediaSearchText) {
      this.mediaSearchText = '';
      localStorage.setItem(MEDIA_SEARCH_STORAGE_KEY, '');
    }
    this._suppressFilterReload = previousSuppressFilterReload;
    if (searchText) {
      const pendingSearchScrollTop = this.consumeCollectionSearchScrollTop(searchText);
      this.clearPendingCollectionSearchForReturn();
      this._pendingCollectionSearchScrollTop = pendingSearchScrollTop;
    }
    if (this.collectionId === null && searchText && !routeSearchText)
      this.updateCollectionSearchUrl(searchText);
    await this._reload();
    this._ready = true;
  }

  public openExportModal(): void {
    if (this.isRootView || this.exportInProgress) return;
    this.exportExcludeVideos = true;
    this.exportJobId = null;
    this.exportStatus = null;
    this.exportError = '';
    this.showExportModal = true;
  }

  public closeExportModal(): void {
    if (this.exportInProgress) return;
    this.showExportModal = false;
    this._exportPollVersion++;
  }

  public async startExport(): Promise<void> {
    if (this.isRootView || !this.collectionId || this.exportStarting || this.exportInProgress) return;

    this.exportStarting = true;
    this.exportError = '';
    try {
      const { jobId } = await startCollectionExport(this.collectionId, this.exportExcludeVideos);
      this.exportJobId = jobId;
      this.exportStatus = {
        jobId,
        state: 'preparing',
        filesProcessed: 0,
        filesTotal: 0,
        error: null,
      };
      const pollVersion = ++this._exportPollVersion;
      void this._pollExportStatus(jobId, pollVersion);
    } catch (error) {
      this.exportError = error instanceof Error ? error.message : String(error);
    } finally {
      this.exportStarting = false;
    }
  }

  private async _pollExportStatus(jobId: string, pollVersion: number): Promise<void> {
    while (pollVersion === this._exportPollVersion && this.exportJobId === jobId) {
      try {
        const status = await getCollectionExportStatus(jobId);
        if (pollVersion !== this._exportPollVersion || this.exportJobId !== jobId) return;
        this.exportStatus = status;
        if (status.state === 'ready' || status.state === 'failed') {
          if (status.state === 'failed')
            this.exportError = status.error ?? this.i18n.tr('collections.export.failed');
          return;
        }
      } catch (error) {
        if (pollVersion !== this._exportPollVersion || this.exportJobId !== jobId) return;
        const message = error instanceof Error ? error.message : String(error);
        this.exportError = message;
        this.exportStatus = {
          jobId,
          state: 'failed',
          filesProcessed: 0,
          filesTotal: 0,
          error: message,
        };
        return;
      }

      await new Promise<void>(resolve => setTimeout(resolve, 750));
    }
  }

  public attached(): void {
    document.addEventListener('click', this._selectionDocumentHandler);
    document.addEventListener('click', this._titlebarOverflowDocumentHandler);
    this.scrollContainer?.addEventListener('scroll', this._visibleRangeScrollHandler, { passive: true });
    window.addEventListener('resize', this._visibleRangeResizeHandler);
    if (this.scrollContainer) {
      this._visibleRangeResizeObserver = new ResizeObserver(() => this.scheduleVisibleMediaRangeUpdate());
      this._visibleRangeResizeObserver.observe(this.scrollContainer);
    }
    if (this.scrollSentinel && this.scrollContainer) {
      this._scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) void this._loadMore();
        },
        { root: this.scrollContainer, threshold: 0.1 },
      );
      this._scrollObserver.observe(this.scrollSentinel);
    }
    void this.restorePendingCollectionSearchScrollTop();
    this.scheduleVisibleMediaRangeUpdate();
  }

  public detaching(): void {
    document.removeEventListener('click', this._selectionDocumentHandler);
    document.removeEventListener('click', this._titlebarOverflowDocumentHandler);
    this.scrollContainer?.removeEventListener('scroll', this._visibleRangeScrollHandler);
    window.removeEventListener('resize', this._visibleRangeResizeHandler);
    this._visibleRangeResizeObserver?.disconnect();
    this._visibleRangeResizeObserver = null;
    if (this._visibleRangeFrame !== null) {
      cancelAnimationFrame(this._visibleRangeFrame);
      this._visibleRangeFrame = null;
    }
    this.titlebarOverflowOpen = false;
    this._reloadVersion++;
    // In-flight reloads intentionally skip their stale cleanup after this version changes.
    // Clear the view state here so a cached mobile view cannot retain a spinner after navigation.
    this.isLoading = false;
    this.isRefreshing = false;
    this.isLoadingMore = false;
    this._exportPollVersion++;
    this._importPollVersion++;
    if (this._searchDebounceHandle !== null) {
      window.clearTimeout(this._searchDebounceHandle);
      this._searchDebounceHandle = null;
    }
    this._scrollObserver?.disconnect();
    this._scrollObserver = null;
    this._localeSub?.dispose();
    this._permissionsSub?.dispose();
  }

  public openImportFilePicker(): void {
    if (this.importInProgress) return;
    this.importFileInput?.click();
  }

  public async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.importParentCollectionId = this.isCollectionSearchMode ? null : this.collectionId;
    this.importInspection = null;
    this.importMappings = {};
    this.resetImportPersonSearch();
    this.importStatus = null;
    this.importError = '';
    this.importUploadProgress = 0;
    this.importStarting = false;
    this.showImportModal = true;
    this.importUploading = true;

    try {
      const inspection = await uploadCollectionImport(file, percent => {
        this.importUploadProgress = percent;
      });
      this.importInspection = inspection;
      const mappings: Record<string, string> = {};
      const selectedCandidates: Record<string, ImportedPersonCandidate> = {};
      const personOptions: Record<string, ImportedPersonCandidate[]> = {};
      for (const person of inspection.persons) {
        const defaultTargetId = person.knownTargetPersonId
          ?? (person.sameNameCandidates.length === 1 ? person.sameNameCandidates[0].id : null);
        mappings[person.id] = defaultTargetId ?? 'new';
        const selectedCandidate = person.sameNameCandidates.find(candidate => candidate.id === defaultTargetId);
        if (selectedCandidate) selectedCandidates[person.id] = selectedCandidate;
        personOptions[person.id] = [...person.sameNameCandidates];
      }
      this.importMappings = mappings;
      this.importSelectedCandidates = selectedCandidates;
      this.importPersonOptions = personOptions;
    } catch (error) {
      this.importError = error instanceof Error ? error.message : String(error);
    } finally {
      this.importUploading = false;
    }
  }

  public closeImportModal(): void {
    if (this.importInProgress) return;
    this.showImportModal = false;
    this._importPollVersion++;
    this.importParentCollectionId = null;
    this.resetImportPersonSearch();
  }

  public setImportMapping(personId: string, event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    const value = target.value;
    if (value === 'search') {
      const previousValue = this.importMappings[personId] ?? 'new';
      target.value = previousValue;
      this.importMappings = { ...this.importMappings, [personId]: previousValue };
      this.importPersonSearchPersonId = personId;
      this.showImportPersonSearch = true;
      return;
    }

    this.importMappings = { ...this.importMappings, [personId]: value };

    const nextSelectedCandidates = { ...this.importSelectedCandidates };
    if (value === 'new') {
      delete nextSelectedCandidates[personId];
    } else {
      const candidate = (this.importPersonOptions[personId] ?? []).find(option => option.id === value);
      if (candidate) nextSelectedCandidates[personId] = candidate;
    }
    this.importSelectedCandidates = nextSelectedCandidates;
  }

  public closeImportPersonSearch(): void {
    this.showImportPersonSearch = false;
    this.importPersonSearchPersonId = null;
  }

  public onImportPersonSearchSelected(person: Person): void {
    const sourcePersonId = this.importPersonSearchPersonId;
    if (!sourcePersonId) return;

    const candidate = this.toImportPersonCandidate(person);
    this.importSelectedCandidates = {
      ...this.importSelectedCandidates,
      [sourcePersonId]: candidate,
    };
    // Add the searched person to this row's own option list (bound directly in the template as
    // `importPersonOptions[person.id]`) so the select actually has a matching <option> to select
    // — a call-expression binding like `importCandidatesForPerson(person)` would not reliably
    // re-evaluate from this internal state change (see the field comment on importPersonOptions).
    const existingOptions = this.importPersonOptions[sourcePersonId] ?? [];
    const dedupedOptions = existingOptions.filter(option => option.id !== candidate.id);
    this.importPersonOptions = {
      ...this.importPersonOptions,
      [sourcePersonId]: [...dedupedOptions, candidate],
    };
    this.importMappings = { ...this.importMappings, [sourcePersonId]: candidate.id };
    this.closeImportPersonSearch();
  }

  private toImportPersonCandidate(person: Person): ImportedPersonCandidate {
    return {
      id: person.id,
      firstName: person.firstName,
      lastName: person.lastName,
      nickName: person.nickName ?? null,
      birthYear: person.birthYear ?? null,
      birthMonth: person.birthMonth ?? null,
      birthDay: person.birthDay ?? null,
      usageCount: person.usageCount ?? 0,
    };
  }

  private resetImportPersonSearch(): void {
    this.showImportPersonSearch = false;
    this.importPersonSearchPersonId = null;
    this.importSelectedCandidates = {};
    this.importPersonOptions = {};
  }

  public importSampleUrl(sample: ImportedMarkerSample): string {
    return this.importInspection
      ? getCollectionImportMediaUrl(this.importInspection.jobId, sample.mediaItemId)
      : '';
  }

  public importPersonDisplayName(person: ImportedPersonInspection): string {
    return [person.firstName, person.lastName].filter(Boolean).join(' ') || person.id;
  }

  public importCandidateDisplayName(candidate: ImportedPersonCandidate): string {
    const name = [candidate.firstName, candidate.lastName].filter(Boolean).join(' ');
    return candidate.nickName ? `${name} (${candidate.nickName})` : name;
  }

  public importSampleIsVideo(sample: ImportedMarkerSample): boolean {
    return sample.mediaType === MediaType.Video;
  }

  private importSampleCropRect(sample: ImportedMarkerSample): FaceRect {
    return getFaceCropRect(
      {
        x: sample.x,
        y: sample.y,
        width: sample.width,
        height: sample.height,
      },
      sample.widthPx ?? 0,
      sample.heightPx ?? 0,
      getFaceZoomThresholdPx(),
    );
  }

  public importSampleContainerStyle(sample: ImportedMarkerSample): string {
    const cropRect = this.importSampleCropRect(sample);
    return facePreviewContainerStyle(cropRect, sample.widthPx ?? 0, sample.heightPx ?? 0, 132);
  }

  public importSampleMediaStyle(sample: ImportedMarkerSample): string {
    return faceCropStyle(this.importSampleCropRect(sample));
  }

  public async startImport(): Promise<void> {
    if (!this.importInspection || this.importStarting || this.importInProgress) return;

    const mappings = this.importInspection.persons.map(person => {
      const value = this.importMappings[person.id] ?? 'new';
      return {
        sourcePersonId: person.id,
        targetPersonId: value === 'new' ? null : value,
        createNew: value === 'new',
      };
    });
    for (const [sourcePersonId, targetPersonId] of Object.entries(this.importInspection.knownMappings ?? {})) {
      mappings.push({
        sourcePersonId,
        targetPersonId,
        createNew: false,
      });
    }

    this.importStarting = true;
    this.importError = '';
    try {
      const { jobId } = await startCollectionImport(
        this.importInspection.jobId,
        mappings,
        this.importParentCollectionId,
      );
      this.importStatus = {
        jobId,
        state: 'importing',
        collectionsProcessed: 0,
        collectionsTotal: this.importInspection.collectionCount,
        mediaItemsProcessed: 0,
        mediaItemsTotal: this.importInspection.mediaItemCount,
        markersProcessed: 0,
        markersTotal: this.importInspection.personMarkerCount,
        error: null,
      };
      const pollVersion = ++this._importPollVersion;
      void this._pollImportStatus(jobId, pollVersion);
    } catch (error) {
      this.importError = error instanceof Error ? error.message : String(error);
    } finally {
      this.importStarting = false;
    }
  }

  private async _pollImportStatus(jobId: string, pollVersion: number): Promise<void> {
    while (pollVersion === this._importPollVersion && this.importInspection?.jobId === jobId) {
      try {
        const status = await getCollectionImportStatus(jobId);
        if (pollVersion !== this._importPollVersion || this.importInspection?.jobId !== jobId) return;
        this.importStatus = status;
        if (status.state === 'completed' || status.state === 'failed') {
          if (status.state === 'failed')
            this.importError = status.error ?? this.i18n.tr('collections.import.failed');
          if (status.state === 'completed')
            await this._reload(true);
          return;
        }
      } catch (error) {
        if (pollVersion !== this._importPollVersion || this.importInspection?.jobId !== jobId) return;
        const message = error instanceof Error ? error.message : String(error);
        this.importError = message;
        this.importStatus = {
          jobId,
          state: 'failed',
          collectionsProcessed: 0,
          collectionsTotal: 0,
          mediaItemsProcessed: 0,
          mediaItemsTotal: 0,
          markersProcessed: 0,
          markersTotal: 0,
          error: message,
        };
        return;
      }

      await new Promise<void>(resolve => setTimeout(resolve, 750));
    }
  }

  private async fetchCollectionWindow(
    collectionId: string,
    sortBy: string,
    sortDescending: boolean,
    preserveScrollPosition: boolean,
    previousMediaCount: number,
  ): Promise<{ detail: CollectionDetail; page: number }> {
    // Reload the same number of normal pages instead of using one larger page size; the next
    // _loadMore request relies on page offsets based on this.pageSize.
    const pagesToLoad = preserveScrollPosition
      ? Math.max(1, Math.ceil(previousMediaCount / this.pageSize))
      : 1;
    let firstDetail: CollectionDetail | null = null;
    let mediaItems: MediaItem[] = [];
    let mediaItemsTotalCount = 0;
    let loadedPage = 0;

    for (let page = 1; page <= pagesToLoad; page++) {
      const result = await getCollection(collectionId, {
        sortBy,
        sortDescending,
        page,
        pageSize: this.pageSize,
        collectionSearchText: this.collectionSearchText.trim() || undefined,
        mediaSearchText: this.mediaSearchText.trim() || undefined,
        onlyFolded: this.onlyFolded,
      });
      firstDetail ??= result;
      mediaItems = [...mediaItems, ...result.mediaItems];
      mediaItemsTotalCount = result.mediaItemsTotalCount;
      loadedPage = page;

      if (!preserveScrollPosition
        || mediaItems.length >= previousMediaCount
        || mediaItems.length >= mediaItemsTotalCount
        || result.mediaItems.length === 0) {
        break;
      }
    }

    if (!firstDetail) {
      throw new Error('The collection response did not contain any data.');
    }

    return {
      detail: preserveScrollPosition
        ? {
          ...firstDetail,
          mediaItems,
          mediaItemsTotalCount,
        }
        : firstDetail,
      page: loadedPage,
    };
  }

  private async fetchAllCollectionSearchResults(
    searchText: string,
  ): Promise<{ results: CollectionSearchResult[]; totalCount: number }> {
    const pageSize = 100;
    const results: CollectionSearchResult[] = [];
    let page = 1;
    let totalCount = 0;

    while (true) {
      const response = await searchCollections(searchText, page, pageSize);
      results.push(...response.results);
      totalCount = response.totalCount;

      if (results.length >= totalCount || response.results.length === 0) break;
      page++;
    }

    return { results, totalCount };
  }

  private async _reload(preserveScrollPosition = false): Promise<void> {
    const reloadVersion = ++this._reloadVersion;
    const requestedCollectionId = this.collectionId;
    const shouldPreserveScrollPosition = preserveScrollPosition
      && !this.isLoading
      && !this.isSelectingAll;
    const scrollAnchor = shouldPreserveScrollPosition
      ? this.captureCollectionScrollAnchor()
      : null;
    const previousMediaCount = shouldPreserveScrollPosition && requestedCollectionId
      ? this.mediaItems.length
      : 0;
    const activeElement = document.activeElement;
    const hadTileFocus = !!this.galleryGridEl
      && activeElement instanceof HTMLElement
      && this.galleryGridEl.contains(activeElement)
      && activeElement.classList.contains('gallery-tile');
    this.isLoading = !shouldPreserveScrollPosition;
    this.isRefreshing = shouldPreserveScrollPosition;
    this.isLoadingMore = false;
    this.error = '';
    this.clearSelection();
    this.page = 1;
    if (!shouldPreserveScrollPosition) {
      this.focusedIndex = 0;
      this.tileHasFocus = false;
    }
    let loaded = false;
    try {
      if (this.isCollectionSearchMode) {
        const result = await this.fetchAllCollectionSearchResults(this.collectionSearchText.trim());
        if (reloadVersion !== this._reloadVersion || requestedCollectionId !== this.collectionId) return;
        this.collectionSearchResults = result.results;
        this.collectionSearchTotalCount = result.totalCount;
        this.collectionSearchPaths = Object.fromEntries(
          result.results.map(searchResult => [
            searchResult.collection.id,
            searchResult.breadcrumb.length > 0
              ? searchResult.breadcrumb.map(ref => ref.title).join(' › ')
              : this.i18n.tr('collections.search.topLevel'),
          ]),
        );
        this.rootCollections = [];
        this.detail = null;
      } else if (requestedCollectionId) {
        const [sortBy, direction] = this.sortOption.split(':');
        const result = await this.fetchCollectionWindow(
          requestedCollectionId,
          sortBy,
          direction !== 'asc',
          shouldPreserveScrollPosition,
          previousMediaCount,
        );
        if (reloadVersion !== this._reloadVersion || requestedCollectionId !== this.collectionId) return;
        this.detail = result.detail;
        this.page = result.page;
        this.rootCollections = [];
      } else {
        const rootCollections = await listRootCollections();
        if (reloadVersion !== this._reloadVersion || requestedCollectionId !== this.collectionId) return;
        this.rootCollections = rootCollections;
        this.collectionSearchResults = [];
        this.collectionSearchTotalCount = 0;
        this.collectionSearchPaths = {};
        this.detail = null;
      }
      loaded = true;
      if (shouldPreserveScrollPosition && scrollAnchor) {
        await this.restoreCollectionScrollAnchor(scrollAnchor);
      }
      if (loaded && this.isCollectionSearchMode)
        await this.restorePendingCollectionSearchScrollTop();
    } catch (e) {
      if (reloadVersion !== this._reloadVersion || requestedCollectionId !== this.collectionId) return;
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      if (reloadVersion === this._reloadVersion) {
        this.isLoading = false;
        this.isRefreshing = false;
        this.scheduleVisibleMediaRangeUpdate();
        const hasTiles = this.isCollectionSearchMode
          ? this.collectionSearchResults.length > 0
          : this.collectionId === null
          ? this.rootCollections.length > 0
          : this.childCollections.length + this.mediaItems.length > 0;
        if (!shouldPreserveScrollPosition && loaded && hadTileFocus && this.lightboxItems.length === 0 && hasTiles) {
          requestAnimationFrame(() => {
            if (reloadVersion !== this._reloadVersion || this.lightboxItems.length > 0) return;
            const tileCount = this.galleryTileCount;
            if (tileCount > 0) void this.focusTile(Math.min(this.focusedIndex, tileCount - 1));
          });
        }
      }
    }
  }

  private async _loadMore(allowWhileSelectingAll = false): Promise<void> {
    if (this.isLoading || this.isRefreshing || this.isLoadingMore || this.isCollectionSearchMode
      || !this.hasMore || !this.collectionId
      || (this.isSelectingAll && !allowWhileSelectingAll)) return;
    const requestedCollectionId = this.collectionId;
    const reloadVersion = this._reloadVersion;
    this.isLoadingMore = true;
    this.page++;
    try {
      const [sortBy, direction] = this.sortOption.split(':');
      const next = await getCollection(requestedCollectionId, {
        sortBy,
        sortDescending: direction !== 'asc',
        page: this.page,
        pageSize: this.pageSize,
        collectionSearchText: this.collectionSearchText.trim() || undefined,
        mediaSearchText: this.mediaSearchText.trim() || undefined,
        onlyFolded: this.onlyFolded,
      });
      if (reloadVersion !== this._reloadVersion || requestedCollectionId !== this.collectionId) return;
      if (this.detail) {
        const existingIds = new Set(this.detail.mediaItems.map(item => item.id));
        const newItems = next.mediaItems.filter(item => !existingIds.has(item.id));
        this.detail = {
          ...this.detail,
          mediaItems: [...this.detail.mediaItems, ...newItems],
          mediaItemsTotalCount: next.mediaItemsTotalCount,
        };
      }
    } catch {
      if (reloadVersion === this._reloadVersion && requestedCollectionId === this.collectionId)
        this.page--;
    } finally {
      if (reloadVersion === this._reloadVersion) {
        this.isLoadingMore = false;
        this.scheduleVisibleMediaRangeUpdate();
      }
    }
  }

  public sortOptionChanged(): void {
    localStorage.setItem(SORT_STORAGE_KEY, this.sortOption);
    if (!this._ready) return;
    void this._reload();
  }

  private scheduleSearchReload(): void {
    if (!this._ready || this._suppressFilterReload) return;
    if (this._searchDebounceHandle !== null) window.clearTimeout(this._searchDebounceHandle);
    this._searchDebounceHandle = window.setTimeout(() => {
      this._searchDebounceHandle = null;
      void this._reload();
    }, SEARCH_DEBOUNCE_MS);
  }

  public collectionSearchTextChanged(): void {
    const searchText = this.collectionSearchText.trim();
    const suppressFilterReload = this._suppressFilterReload;
    localStorage.setItem(COLLECTION_SEARCH_STORAGE_KEY, this.collectionSearchText);
    if (this.isCollectionSearchMode) {
      const previousSuppressFilterReload = this._suppressFilterReload;
      this._suppressFilterReload = true;
      if (this.mediaSearchText) {
        this.mediaSearchText = '';
        localStorage.setItem(MEDIA_SEARCH_STORAGE_KEY, '');
      }
      this._suppressFilterReload = previousSuppressFilterReload;
      this.selectMode = false;
      this.clearSelection();
    }
    if (!suppressFilterReload) {
      this.clearPendingCollectionSearchForReturn();
      if (searchText && this.collectionId !== null) {
        void this.router.load('collections', {
          historyStrategy: 'replace',
          queryParams: { [COLLECTION_SEARCH_QUERY_KEY]: searchText },
        });
        return;
      }
      this.updateCollectionSearchUrl(searchText);
    }
    if (!suppressFilterReload) this.scheduleSearchReload();
  }

  public mediaSearchTextChanged(): void {
    localStorage.setItem(MEDIA_SEARCH_STORAGE_KEY, this.mediaSearchText);
    if (this.isRootView) return;
    this.scheduleSearchReload();
  }

  public clearCollectionSearchFilter(): void {
    if (!this.collectionSearchText) return;
    this.collectionSearchText = '';
  }

  public clearMediaSearchFilter(): void {
    if (!this.mediaSearchText) return;
    this.mediaSearchText = '';
  }

  public toggleCollectionFilterBar(): void {
    this.showCollectionFilterBar = !this.showCollectionFilterBar;
    localStorage.setItem(FILTERBAR_STORAGE_KEY, String(this.showCollectionFilterBar));
  }

  public onlyFoldedChanged(): void {
    localStorage.setItem(FOLDED_FILTER_STORAGE_KEY, String(this.onlyFolded));
    if (!this._ready || this.isRootView || this._suppressFilterReload) return;
    void this._reload();
  }

  public clearCollectionFilters(): void {
    if (!this.collectionSearchText.trim() && !this.mediaSearchText.trim() && !this.onlyFolded) return;
    const hadCollectionSearch = this.isCollectionSearchMode;
    if (this._searchDebounceHandle !== null) {
      window.clearTimeout(this._searchDebounceHandle);
      this._searchDebounceHandle = null;
    }
    this._suppressFilterReload = true;
    this.collectionSearchText = '';
    this.mediaSearchText = '';
    this.onlyFolded = false;
    this._suppressFilterReload = false;
    if (hadCollectionSearch) {
      this.clearPendingCollectionSearchForReturn();
      this.updateCollectionSearchUrl('');
    }
    if (this._ready && (hadCollectionSearch || this.collectionId !== null)) void this._reload();
  }

  // Invoked by the lightbox when the user navigates past the last currently-loaded
  // item and `hasMore` is true — reuses the same infinite-scroll pagination as the
  // grid, then refreshes the (snapshot-copied) items array the lightbox is bound to.
  public async requestMoreForLightbox(): Promise<void> {
    await this._loadMore();
    this.lightboxItems = [...this.mediaItems];
  }


  // ── Navigation ────────────────────────────────────────────────────────────

  public async openCollection(id: string): Promise<void> {
    const searchText = this.collectionSearchText.trim();
    if (searchText) this.rememberCollectionSearchForReturn(searchText);
    if (searchText && this.collectionId === id) {
      this.leaveCollectionSearchMode();
      this.rememberCollectionSearchForReturn(searchText);
      await this.router.load(`collections/${id}`, {
        historyStrategy: 'replace',
        transitionPlan: 'invoke-lifecycles',
      });
      return;
    }
    await this.router.load(`collections/${id}`);
  }

  public async navigateHome(): Promise<void> {
    const pendingSearchText = this.pendingCollectionSearchForReturn();
    if (pendingSearchText && !this.isCollectionSearchMode) {
      await this.router.load('collections', {
        queryParams: { [COLLECTION_SEARCH_QUERY_KEY]: pendingSearchText },
      });
      return;
    }
    const wasSearchMode = this.leaveCollectionSearchMode();
    if (wasSearchMode && this.collectionId === null) {
      this.updateCollectionSearchUrl('');
      await this._reload();
      return;
    }
    await this.router.load('collections');
  }

  public async navigateToBreadcrumb(ref: CollectionRef): Promise<void> {
    this.leaveCollectionSearchMode();
    await this.router.load(`collections/${ref.id}`);
  }

  private leaveCollectionSearchMode(): boolean {
    if (!this.isCollectionSearchMode) return false;
    if (this._searchDebounceHandle !== null) {
      window.clearTimeout(this._searchDebounceHandle);
      this._searchDebounceHandle = null;
    }
    this._suppressFilterReload = true;
    this.collectionSearchText = '';
    this._suppressFilterReload = false;
    localStorage.setItem(COLLECTION_SEARCH_STORAGE_KEY, '');
    this.collectionSearchResults = [];
    this.collectionSearchTotalCount = 0;
    this.collectionSearchPaths = {};
    this.clearPendingCollectionSearchForReturn();
    return true;
  }

  // ── Tile helpers ──────────────────────────────────────────────────────────

  public previewUrl(item: MediaItem): string {
    return getMediaPreviewUrl(item.id, item.lastUpdatedAt || item.uploadedAt);
  }

  public isVideo(item: MediaItem): boolean { return item.mediaType === MediaType.Video; }

  public fileSize(item: MediaItem): string { return formatFileSize(item.fileSizeBytes); }

  public toggleTags(): void {
    this.showTags = !this.showTags;
    localStorage.setItem(TAGS_OVERLAY_STORAGE_KEY, String(this.showTags));
  }

  public toggleTime(): void {
    this.showTime = !this.showTime;
    localStorage.setItem(TIME_OVERLAY_STORAGE_KEY, String(this.showTime));
  }

  public toggleFileName(): void {
    this.showFileName = !this.showFileName;
    localStorage.setItem(FILENAME_OVERLAY_STORAGE_KEY, String(this.showFileName));
  }

  public tileTime(item: MediaItem): string {
    if (!item.capturedAt) return '';
    const date = new Date(item.capturedAt);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(date);
  }

  public visibleTileTags(item: MediaItem): TagRef[] { return item.tags.slice(0, MAX_VISIBLE_TILE_TAGS); }

  public hiddenTileTagsCount(item: MediaItem): number {
    return Math.max(0, item.tags.length - MAX_VISIBLE_TILE_TAGS);
  }

  public hiddenTileTagsTitle(item: MediaItem): string {
    return item.tags.slice(MAX_VISIBLE_TILE_TAGS).map(t => t.name).join(', ');
  }

  private getTileElements(): HTMLElement[] {
    if (!this.galleryGridEl) return [];
    return Array.from(this.galleryGridEl.querySelectorAll<HTMLElement>('.gallery-tile'));
  }

  private getCollectionTileKey(tile: HTMLElement | undefined): string | null {
    if (!tile) return null;
    const collectionId = tile.dataset['collectionId'];
    if (collectionId) return `collection:${collectionId}`;
    const mediaId = tile.dataset['mediaId'];
    return mediaId ? `media:${mediaId}` : null;
  }

  private captureCollectionScrollAnchor(): CollectionScrollAnchor {
    const container = this.scrollContainer;
    const tiles = this.getTileElements();
    const fallbackIndex = tiles.length > 0
      ? Math.min(Math.max(this.focusedIndex, 0), tiles.length - 1)
      : 0;
    const focusedTile = this.tileHasFocus ? tiles[this.focusedIndex] : undefined;
    const focusedTileKey = this.getCollectionTileKey(focusedTile);
    if (!container || tiles.length === 0) {
      return {
        tileKey: this.getCollectionTileKey(tiles[fallbackIndex]),
        fallbackIndex,
        offsetTop: 0,
        scrollTop: container?.scrollTop ?? 0,
        focusedTileKey,
        hadTileFocus: this.tileHasFocus,
      };
    }

    const containerRect = container.getBoundingClientRect();
    const firstVisibleIndex = tiles.findIndex(tile => {
      const rect = tile.getBoundingClientRect();
      return rect.bottom > containerRect.top && rect.top < containerRect.bottom;
    });
    const anchorIndex = firstVisibleIndex >= 0 ? firstVisibleIndex : fallbackIndex;
    const anchorTile = tiles[anchorIndex];
    const anchorRect = anchorTile?.getBoundingClientRect();

    return {
      tileKey: this.getCollectionTileKey(anchorTile),
      fallbackIndex: anchorIndex,
      offsetTop: anchorRect ? anchorRect.top - containerRect.top : 0,
      scrollTop: container.scrollTop,
      focusedTileKey,
      hadTileFocus: this.tileHasFocus,
    };
  }

  private async restoreCollectionScrollAnchor(anchor: CollectionScrollAnchor): Promise<void> {
    const container = this.scrollContainer;
    if (!container) return;

    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    const tiles = this.getTileElements();
    if (tiles.length === 0) {
      container.scrollTop = 0;
      this.focusedIndex = 0;
      this.tileHasFocus = false;
      return;
    }

    let targetIndex = anchor.tileKey
      ? tiles.findIndex(tile => this.getCollectionTileKey(tile) === anchor.tileKey)
      : -1;
    if (targetIndex < 0) {
      targetIndex = Math.min(Math.max(anchor.fallbackIndex, 0), tiles.length - 1);
    }

    const focusedIndex = anchor.focusedTileKey
      ? tiles.findIndex(tile => this.getCollectionTileKey(tile) === anchor.focusedTileKey)
      : -1;
    const focusTargetIndex = focusedIndex >= 0 ? focusedIndex : targetIndex;
    const focusTarget = tiles[focusTargetIndex];
    this.focusedIndex = focusTargetIndex;
    if (anchor.hadTileFocus && focusTarget) {
      focusTarget.focus({ preventScroll: true });
      this.tileHasFocus = true;
    } else {
      this.tileHasFocus = false;
    }

    const target = tiles[targetIndex];
    if (target) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      container.scrollTop += targetRect.top - containerRect.top - anchor.offsetTop;
    } else {
      container.scrollTop = anchor.scrollTop;
    }
    this.scheduleVisibleMediaRangeUpdate();
  }

  private async restorePendingCollectionSearchScrollTop(): Promise<void> {
    const scrollTop = this._pendingCollectionSearchScrollTop;
    const container = this.scrollContainer;
    if (scrollTop === null || !container) return;

    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    if (this._pendingCollectionSearchScrollTop !== scrollTop) return;
    container.scrollTop = scrollTop;
    this._pendingCollectionSearchScrollTop = null;
    this.scheduleVisibleMediaRangeUpdate();
  }

  private get galleryTileCount(): number {
    return this.getTileElements().length;
  }

  private scheduleVisibleMediaRangeUpdate(): void {
    if (this._visibleRangeFrame !== null) return;
    this._visibleRangeFrame = requestAnimationFrame(() => {
      this._visibleRangeFrame = null;
      this.updateVisibleMediaRange();
    });
  }

  private updateVisibleMediaRange(): void {
    const container = this.scrollContainer;
    const tiles = this.getTileElements();
    if (this.isLoading || this.isRootView || !container || tiles.length === 0 || this.mediaItems.length === 0) {
      this.visibleMediaStart = 0;
      this.visibleMediaEnd = 0;
      return;
    }

    const mediaIndexById = new Map(this.mediaItems.map((item, index) => [item.id, index]));
    const containerRect = container.getBoundingClientRect();
    const viewportTop = Math.max(containerRect.top, 0);
    const viewportBottom = Math.min(containerRect.bottom, window.innerHeight);
    let firstVisible = -1;
    let lastVisible = -1;

    for (const tile of tiles) {
      const mediaId = tile.dataset['mediaId'];
      if (!mediaId) continue;
      const mediaIndex = mediaIndexById.get(mediaId);
      if (mediaIndex === undefined) continue;
      const rect = tile.getBoundingClientRect();
      if (rect.bottom > viewportTop && rect.top < viewportBottom) {
        if (firstVisible < 0) firstVisible = mediaIndex;
        lastVisible = mediaIndex;
      }
    }

    if (firstVisible < 0) {
      this.visibleMediaStart = 0;
      this.visibleMediaEnd = 0;
      return;
    }

    this.visibleMediaStart = firstVisible + 1;
    this.visibleMediaEnd = lastVisible + 1;
  }

  private getColumnsCount(): number {
    const tiles = this.getTileElements();
    if (tiles.length === 0) return 1;
    const firstTop = tiles[0].offsetTop;
    let count = 0;
    for (const tile of tiles) {
      if (tile.offsetTop !== firstTop) break;
      count++;
    }
    return Math.max(1, count);
  }

  public onGridTileFocus(event: FocusEvent): void {
    const tile = event.currentTarget;
    if (!(tile instanceof HTMLElement)) return;
    const index = this.getTileElements().indexOf(tile);
    if (index < 0) return;
    this.focusedIndex = index;
    this.tileHasFocus = true;
  }

  public onGridTileBlur(): void {
    this.tileHasFocus = false;
  }

  public onGridKeyDown(event: KeyboardEvent): void {
    if (this.lightboxItems.length > 0) return;
    if (event.defaultPrevented) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tile = target.closest<HTMLElement>('.gallery-tile');
    if (!tile || !this.galleryGridEl?.contains(tile)) return;

    const tiles = this.getTileElements();
    const index = tiles.indexOf(tile);
    if (index < 0) return;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        void this.focusTile(index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        void this.focusTile(index - 1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        void this.focusTile(index + this.getColumnsCount());
        break;
      case 'ArrowUp':
        event.preventDefault();
        void this.focusTile(index - this.getColumnsCount());
        break;
      case ' ':
      case 'Spacebar': {
        if (this.isCollectionSearchMode) break;
        const collectionIndex = this.collectionIndexForTile(tile);
        if (collectionIndex >= 0) {
          event.preventDefault();
          const collection = this.visibleCollections[collectionIndex];
          if (collection) this.toggleCollectionSelected(collection, index);
          break;
        }
        const mediaIndex = this.mediaIndexForTile(tile);
        if (mediaIndex < 0) return;
        event.preventDefault();
        this.toggleItemSelected(this.mediaItems[mediaIndex], index);
        break;
      }
      case 'Enter': {
        event.preventDefault();
        const collectionId = tile.dataset['collectionId'];
        if (collectionId) {
          void this.openCollection(collectionId);
          break;
        }
        const mediaIndex = this.mediaIndexForTile(tile);
        if (mediaIndex >= 0) this.openLightbox(mediaIndex);
        break;
      }
      default:
        break;
    }
  }

  private mediaIndexForTile(tile: HTMLElement): number {
    const mediaId = tile.dataset['mediaId'];
    return mediaId ? this.mediaItems.findIndex(item => item.id === mediaId) : -1;
  }

  private collectionIndexForTile(tile: HTMLElement): number {
    const collectionId = tile.dataset['collectionId'];
    return collectionId
      ? this.visibleCollections.findIndex(collection => collection.id === collectionId)
      : -1;
  }

  private async focusTile(index: number): Promise<void> {
    let tiles = this.getTileElements();
    if (index >= tiles.length && this.hasMore && !this.isLoadingMore) {
      await this._loadMore();
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      tiles = this.getTileElements();
    }
    if (tiles.length === 0) return;
    const clamped = Math.max(0, Math.min(index, tiles.length - 1));
    this.focusedIndex = clamped;
    const target = tiles[clamped];
    target?.focus();
    target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  public toggleSelectMode(): void {
    if (this.isCollectionSearchMode || this.isSelectingAll || this.movingSelection) return;
    this.selectMode = !this.selectMode;
    if (!this.selectMode) this.clearSelection();
  }

  public toggleTitlebarOverflow(event: Event): void {
    event.stopPropagation();
    this.titlebarOverflowOpen = !this.titlebarOverflowOpen;
  }

  public closeTitlebarOverflow(): void {
    this.titlebarOverflowOpen = false;
  }

  public onTitlebarOverflowKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeTitlebarOverflow();
    this.titlebarOverflowHost?.querySelector<HTMLButtonElement>('.collections-titlebar-overflow__toggle')?.focus();
  }

  public clearSelection(): void {
    this.selectedIds = {};
    this.selectedCollectionIds = {};
    this._lastSelectedIdx = -1;
    this.rangeSelectionMode = false;
    this.selectionActionsOpen = false;
  }

  public toggleSelectionActions(event: Event): void {
    event.stopPropagation();
    if (!this.hasSelection || this.isSelectingAll || this.movingSelection) return;
    this.selectionActionsOpen = !this.selectionActionsOpen;
  }

  public onSelectionActionsKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.selectionActionsOpen = false;
    this.selectionActionsHost?.querySelector<HTMLButtonElement>('.selection-bar__icon-button')?.focus();
  }

  public runSelectionAction(action: SelectionAction): void {
    if (!this.hasSelection || this.isSelectingAll || this.movingSelection) return;
    this.selectionActionsOpen = false;
    switch (action) {
      case 'collection':
        this.openPickerForSelection();
        break;
      case 'move':
        this.openMovePickerForSelection();
        break;
      case 'family':
        void this.setSelectedVisibility('family');
        break;
      case 'private':
        void this.setSelectedVisibility('private');
        break;
      case 'remove':
        void this.removeSelectedFromCollection();
        break;
      case 'fold':
        this.openFoldModal();
        break;
      case 'date':
        this.openBulkDateModal();
        break;
      case 'chronology':
        this.openChronologyOrderModal();
        break;
      case 'transfer':
        this.openOwnerPicker();
        break;
    }
  }

  public openBulkDateModal(): void {
    if (!this.canSetSelectedDate || this.bulkDateUpdating || this.isSelectingAll) return;
    this.showBulkDateModal = true;
  }

  public closeBulkDateModal(): void {
    if (!this.bulkDateUpdating) this.showBulkDateModal = false;
  }

  public openChronologyOrderModal(): void {
    if (!this.canOrderSelectionChronologically || this.chronologyOrderUpdating || this.isSelectingAll) return;
    this.chronologyOrderItems = [...this.selectedMediaItems];
    this.showChronologyOrderModal = true;
  }

  public closeChronologyOrderModal(): void {
    if (this.chronologyOrderUpdating) return;
    this.showChronologyOrderModal = false;
    this.chronologyOrderItems = [];
  }

  public async confirmBulkDate(localDate: string): Promise<void> {
    if (this.bulkDateUpdating || !this.canSetSelectedDate) return;

    const items = [...this.selectedMediaItems];
    try {
      const undatedItems = items.filter(item => !item.capturedAt);
      const updates = items.map(item => ({
        mediaItemId: item.id,
        capturedAt: this.buildCapturedAtForDate(
          item,
          localDate,
          item.capturedAt ? 0 : undatedItems.indexOf(item),
          item.capturedAt ? 0 : undatedItems.length,
        ),
      }));

      this.bulkDateUpdating = true;
      await bulkUpdateCapturedAt({
        updates,
        mode: 'date',
        localDate,
        localUtcOffsetMinutes: -new Date(`${localDate}T12:00:00`).getTimezoneOffset(),
      });
      this.showBulkDateModal = false;
      this.selectMode = false;
      this.clearSelection();
      await this._reload(true);
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('gallery.bulkDate.failed'),
      );
    } finally {
      this.bulkDateUpdating = false;
    }
  }

  public async confirmChronologyOrder(items: MediaItem[]): Promise<void> {
    if (this.chronologyOrderUpdating || !this.canOrderSelectionChronologically) return;

    const orderedItems = [...items];
    const localDate = orderedItems[0]?.capturedAt
      ? this.localDateKey(new Date(orderedItems[0].capturedAt))
      : null;
    if (!localDate || orderedItems.length < 2) return;

    try {
      const updates = orderedItems.map((item, index) => ({
        mediaItemId: item.id,
        capturedAt: this.buildCapturedAtForDate(item, localDate, index, orderedItems.length, true),
      }));

      this.chronologyOrderUpdating = true;
      await bulkUpdateCapturedAt({
        updates,
        mode: 'chronological',
        localDate,
        localUtcOffsetMinutes: -new Date(`${localDate}T12:00:00`).getTimezoneOffset(),
      });
      this.showChronologyOrderModal = false;
      this.chronologyOrderItems = [];
      this.selectMode = false;
      this.clearSelection();
      await this._reload(true);
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('gallery.chronologyOrder.failed'),
      );
    } finally {
      this.chronologyOrderUpdating = false;
    }
  }

  private localDateKey(date: Date): string {
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildCapturedAtForDate(
    item: MediaItem,
    localDate: string,
    sequenceIndex: number,
    sequenceCount: number,
    forceSequentialTime = false,
  ): string {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
    if (!parts) throw new Error(this.i18n.tr('gallery.bulkDate.invalidDate'));

    const year = Number(parts[1]);
    const month = Number(parts[2]) - 1;
    const day = Number(parts[3]);
    const existing = item.capturedAt ? new Date(item.capturedAt) : null;
    if (existing && Number.isNaN(existing.getTime()))
      throw new Error(this.i18n.tr('gallery.bulkDate.invalidDate'));

    const sortDescending = this.sortOption.endsWith(':desc');
    const seconds = forceSequentialTime || !existing
      ? (sortDescending ? sequenceCount - 1 - sequenceIndex : sequenceIndex)
      : 0;
    const preserveExistingTime = !!existing && !forceSequentialTime;
    const target = new Date(
      year,
      month,
      day,
      preserveExistingTime ? existing.getHours() : 0,
      preserveExistingTime ? existing.getMinutes() : 0,
      (preserveExistingTime ? existing.getSeconds() : 0) + seconds,
      preserveExistingTime ? existing.getMilliseconds() : 0,
    );
    return target.toISOString();
  }

  public openFoldModal(): void {
    if (!this.canFoldSelection) return;
    this.foldItems = [...this.selectedMediaItems];
    this.showFoldModal = true;
  }

  public closeFoldModal(): void {
    if (this.folding) return;
    this.showFoldModal = false;
    this.foldItems = [];
  }

  public async confirmFold(representativeId: string): Promise<void> {
    if (this.folding || !this.canFoldSelection) return;

    this.folding = true;
    try {
      await foldMediaItems(this.foldItems.map(item => item.id), representativeId);
      this.showFoldModal = false;
      this.foldItems = [];
      this.selectMode = false;
      await this._reload(true);
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('gallery.folding.failed'),
      );
    } finally {
      this.folding = false;
    }
  }

  public openOwnerPicker(): void {
    if (!this.canTransferSelection || this.transferring || this.isSelectingAll) return;
    this.showOwnerPicker = true;
  }

  public closeOwnerPicker(): void {
    if (this.transferring) return;
    this.showOwnerPicker = false;
  }

  public userLabel(user: AppUser): string {
    const displayName = user.displayName?.trim();
    return displayName && displayName !== user.username
      ? `${displayName} (${user.username})`
      : user.username;
  }

  public async confirmOwnerTransfer(target: AppUser): Promise<void> {
    if (this.transferring || !this.canTransferSelection) return;

    const ids = [...this.selectedMediaIds];
    if (ids.length === 0) return;

    this.showOwnerPicker = false;
    const targetLabel = this.userLabel(target);
    const message = ids.length === 1
      ? this.i18n.tr('gallery.selection.transferOwnerConfirmOne', { target: targetLabel })
      : this.i18n.tr('gallery.selection.transferOwnerConfirmMany', { count: ids.length, target: targetLabel });
    const confirmed = await ConfirmDialog.confirm(
      message,
      this.i18n.tr('gallery.selection.transferOwnerTitle'),
    );
    if (!confirmed) return;

    this.transferring = true;
    try {
      const result = await transferMediaOwnership(ids, target.id);
      this.selectMode = false;
      await this._reload(true);
      const successKey = result.transferredCount === 0
        ? 'gallery.selection.transferOwnerSuccessNone'
        : result.transferredCount === 1
        ? 'gallery.selection.transferOwnerSuccessOne'
        : 'gallery.selection.transferOwnerSuccessMany';
      await ConfirmDialog.alert(
        this.i18n.tr(successKey, { count: result.transferredCount, target: targetLabel }),
        this.i18n.tr('gallery.selection.transferOwnerTitle'),
      );
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : this.i18n.tr('gallery.selection.transferOwnerFailed'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.transferring = false;
    }
  }

  public async selectAllVisible(): Promise<void> {
    if (this.isCollectionSearchMode || this.isSelectingAll || this.isLoading
      || this.isRefreshing || this.isLoadingMore) return;

    const collectionCount = this.visibleCollections.length;
    const totalToSelect = collectionCount + this.mediaItemsTotalCount;
    if (totalToSelect > SELECT_ALL_CONFIRM_THRESHOLD) {
      const confirmed = await ConfirmDialog.confirm(
        this.i18n.tr('collections.selection.selectAllConfirm', { count: totalToSelect }),
        this.i18n.tr('collections.selection.selectAllTitle'),
      );
      if (!confirmed) return;
    }

    this.isSelectingAll = true;
    this.selectAllProgress = collectionCount + Math.min(this.mediaItems.length, this.mediaItemsTotalCount);
    this.selectAllTotal = totalToSelect;

    try {
      while (this.hasMore) {
        const loadedBefore = this.mediaItems.length;
        await this._loadMore(true);
        this.selectAllTotal = collectionCount + this.mediaItemsTotalCount;
        this.selectAllProgress = collectionCount + Math.min(this.mediaItems.length, this.mediaItemsTotalCount);

        if (this.mediaItems.length <= loadedBefore) {
          if (!this.hasMore) break;
          throw new Error(this.i18n.tr('collections.selection.selectAllFailed'));
        }
      }

      const nextMedia: Record<string, boolean> = {};
      for (const item of this.mediaItems) nextMedia[item.id] = true;
      const nextCollections: Record<string, boolean> = {};
      for (const collection of this.visibleCollections) nextCollections[collection.id] = true;
      this.selectedIds = nextMedia;
      this.selectedCollectionIds = nextCollections;
      this.selectMode = true;
      this._lastSelectedIdx = -1;
      this.rangeSelectionMode = false;
      this.selectAllProgress = this.selectionCount;
      this.selectAllTotal = this.selectionCount;
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('collections.selection.selectAllTitle'),
      );
    } finally {
      this.isSelectingAll = false;
    }
  }

  public onCollectionTileClick(collection: Collection, tileIndex: number, event: MouseEvent): void {
    if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
      return;
    }
    if (event.shiftKey && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
      return;
    }
    if (this.selectMode || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) this.toggleCollectionSelected(collection, tileIndex);
      else this.selectSingleCollection(collection, tileIndex);
      return;
    }
    void this.openCollection(collection.id);
  }

  public toggleCollectionCheckbox(collection: Collection, tileIndex: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
    } else if (event.shiftKey && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
    } else {
      this.toggleCollectionSelected(collection, tileIndex);
    }
  }

  public onMediaTileClick(item: MediaItem, tileIndex: number, event: MouseEvent): void {
    if (this.consumePenTapClick(event)) return;
    if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
      return;
    }
    if (event.shiftKey && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
      return;
    }
    if (this.selectMode || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) this.toggleItemSelected(item, tileIndex);
      else this.selectSingleItem(item, tileIndex);
      return;
    }
    // Plain click only focuses the tile (native button focus already handles this);
    // track it as the shift-click anchor for range selection.
    this._lastSelectedIdx = tileIndex;
    this.focusedIndex = tileIndex;
  }

  public onMediaTileDoubleClick(index: number, event: MouseEvent): void {
    if (this._touchDoubleTapHandledAt > 0) {
      const elapsed = Date.now() - this._touchDoubleTapHandledAt;
      this._touchDoubleTapHandledAt = 0;
      if (elapsed <= TOUCH_TAP_MAX_DELAY_MS) {
        event.preventDefault();
        return;
      }
    }
    event.preventDefault();
    this.openLightbox(index);
  }

  public onTilePointerDown(index: number, event: PointerEvent): void {
    if (!this.isTouchLikePointer(event)) return;
    if (this.isSelectionCheckboxTarget(event.target)) {
      this._touchTapStart = null;
      this._lastTouchTap = null;
      return;
    }
    this._touchTapStart = { index, x: event.clientX, y: event.clientY };
  }

  public onTilePointerUp(index: number, event: PointerEvent): void {
    if (!this.isTouchLikePointer(event)) return;
    const start = this._touchTapStart;
    this._touchTapStart = null;
    if (!start || start.index !== index || this.isSelectionCheckboxTarget(event.target)) {
      this._lastTouchTap = null;
      return;
    }

    const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (moved > TOUCH_TAP_MAX_MOVEMENT_PX) {
      this._lastTouchTap = null;
      return;
    }

    if (event.pointerType === 'pen') {
      this._lastTouchTap = null;
      if (!this.selectMode) {
        const now = Date.now();
        this._penTapHandledAt = now;
        this._touchDoubleTapHandledAt = now;
        event.preventDefault();
        this.openLightbox(index);
      }
      return;
    }

    const now = Date.now();
    if (this._lastTouchTap
      && this._lastTouchTap.index === index
      && now - this._lastTouchTap.at <= TOUCH_TAP_MAX_DELAY_MS) {
      this._lastTouchTap = null;
      this._touchDoubleTapHandledAt = now;
      event.preventDefault();
      this.openLightbox(index);
      return;
    }
    this._lastTouchTap = { index, at: now };
  }

  public onTilePointerCancel(event: PointerEvent): void {
    if (!this.isTouchLikePointer(event)) return;
    this._touchTapStart = null;
    this._lastTouchTap = null;
  }

  private isTouchLikePointer(event: PointerEvent): boolean {
    return event.pointerType === 'touch' || event.pointerType === 'pen';
  }

  private consumePenTapClick(event: MouseEvent): boolean {
    if (this._penTapHandledAt <= 0) return false;
    const elapsed = Date.now() - this._penTapHandledAt;
    this._penTapHandledAt = 0;
    if (elapsed > TOUCH_TAP_MAX_DELAY_MS) return false;
    event.preventDefault();
    return true;
  }

  private isSelectionCheckboxTarget(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('.gallery-tile__checkbox');
  }

  public toggleTileCheckbox(item: MediaItem, tileIndex: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
    } else if (event.shiftKey && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectTileRange(this._lastSelectedIdx, tileIndex);
    } else {
      this.toggleItemSelected(item, tileIndex);
    }
  }

  private selectTileRange(anchorIndex: number, targetIndex: number): void {
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    const nextMedia: Record<string, boolean> = {};
    const nextCollections: Record<string, boolean> = {};
    const tiles = this.gridTiles;
    for (let i = start; i <= end; i++) {
      const tile = tiles[i];
      if (!tile) continue;
      if (tile.kind === 'collection') nextCollections[tile.collection.id] = true;
      else nextMedia[tile.item.id] = true;
    }
    this.selectedIds = nextMedia;
    this.selectedCollectionIds = nextCollections;
    this.selectMode = true;
    this.focusedIndex = targetIndex;
    this.rangeSelectionMode = false;
  }

  public toggleRangeSelectionMode(): void {
    if (this.isSelectingAll || !this.canSelectRange) return;
    this.rangeSelectionMode = !this.rangeSelectionMode;
  }

  private selectSingleItem(item: MediaItem, tileIndex: number): void {
    this.selectedIds = { [item.id]: true };
    this.selectedCollectionIds = {};
    this._lastSelectedIdx = tileIndex;
    this.focusedIndex = tileIndex;
    this.selectMode = true;
    this.rangeSelectionMode = false;
  }

  private toggleItemSelected(item: MediaItem, tileIndex: number): void {
    const next = { ...this.selectedIds };
    if (next[item.id]) delete next[item.id]; else next[item.id] = true;
    this.selectedIds = next;
    this._lastSelectedIdx = tileIndex;
    this.focusedIndex = tileIndex;
    this.selectMode = true;
  }

  private selectSingleCollection(collection: Collection, tileIndex: number): void {
    this.selectedCollectionIds = { [collection.id]: true };
    this.selectedIds = {};
    this._lastSelectedIdx = tileIndex;
    this.focusedIndex = tileIndex;
    this.selectMode = true;
    this.rangeSelectionMode = false;
  }

  private toggleCollectionSelected(collection: Collection, tileIndex: number): void {
    const next = { ...this.selectedCollectionIds };
    if (next[collection.id]) delete next[collection.id]; else next[collection.id] = true;
    this.selectedCollectionIds = next;
    this._lastSelectedIdx = tileIndex;
    this.focusedIndex = tileIndex;
    this.selectMode = true;
  }

  // ── Lightbox ──────────────────────────────────────────────────────────────

  public openLightbox(index: number): void {
    this.lightboxItems = [...this.mediaItems];
    this.lightboxStartIndex = index;
  }

  public closeLightbox(lastItem?: MediaItem | null): void {
    this.lightboxItems = [];
    if (lastItem) {
      const tileIndex = this.gridTiles.findIndex(tile =>
        tile.kind === 'media' && tile.item.id === lastItem.id);
      if (tileIndex >= 0) void this.focusTile(tileIndex);
    }
  }

  public onLightboxSaved(item: MediaItem): void {
    if (!this.detail) return;
    this.detail = {
      ...this.detail,
      mediaItems: this.detail.mediaItems.map(existing => existing.id === item.id ? item : existing),
    };
    this.lightboxItems = this.lightboxItems.map(existing => existing.id === item.id ? item : existing);
  }

  public async onLightboxFoldChanged(
    group: MediaFoldGroup | null,
    updatedItem: MediaItem | null,
    operation: FoldChangeOperation,
  ): Promise<void> {
    this.updateLightboxAfterFoldChange(group, updatedItem, operation);
    await this._reload(true);
  }

  public onLightboxDeleted(_id: string): void {
    // Reload so deleting a representative also reveals its folded children and refreshes
    // the representative count. The same refresh handles deleting a folded child by updating
    // the representative's folded badge.
    this.closeLightbox();
    void this._reload(true);
  }

  private updateLightboxAfterFoldChange(
    group: MediaFoldGroup | null,
    updatedItem: MediaItem | null,
    operation: FoldChangeOperation,
  ): void {
    let items = [...this.lightboxItems];
    if (group) {
      const groupIds = new Set(group.items.map(item => item.id));
      const firstGroupIndex = items.findIndex(item => groupIds.has(item.id));
      items = items.filter(item => !groupIds.has(item.id));
      const replacementItems = operation === 'group'
        ? group.items.map(item => item.id === updatedItem?.id
          ? updatedItem
          : { ...item, foldedIntoMediaItemId: null, foldedImageCount: 0 })
        : operation === 'item'
          ? [
            { ...group.representative, foldedImageCount: Math.max(0, group.representative.foldedImageCount - 1) },
            ...(updatedItem ? [updatedItem] : []),
          ]
          : [group.representative];
      items.splice(firstGroupIndex >= 0 ? firstGroupIndex : items.length, 0, ...replacementItems);
    } else if (updatedItem) {
      const updatedIndex = items.findIndex(item => item.id === updatedItem.id);
      if (updatedIndex >= 0) items[updatedIndex] = updatedItem;
      else items.push(updatedItem);
    }
    this.lightboxItems = items;
  }

  public onLightboxRemovedFromCollection(_id: string): void {
    // Removed from the current collection only — MediaItem itself still exists.
    this.closeLightbox();
    if (!this.detail) return;
    void this._reload(true);
  }

  // ── Collection CRUD ───────────────────────────────────────────────────────

  public openLocationMapModal(collection: Collection, event: Event): void {
    event.stopPropagation();
    this.locationMapCollection = collection;
  }

  public closeLocationMapModal(): void {
    this.locationMapCollection = null;
  }

  public openCreateModal(): void {
    this.collectionToEdit = null;
    this.showCollectionModal = true;
  }

  public async openEditModal(): Promise<void> {
    const currentCollection = this.currentCollection;
    const collectionId = this.collectionId;
    if (!currentCollection || !collectionId || currentCollection.id !== collectionId
      || this.isLoading || this.openingCollectionModal) return;

    this.openingCollectionModal = true;
    try {
      const [sortBy, direction] = this.sortOption.split(':');
      const freshDetail = await getCollection(collectionId, {
        sortBy,
        sortDescending: direction !== 'asc',
        page: 1,
        pageSize: 1,
        collectionSearchText: this.collectionSearchText.trim() || undefined,
        mediaSearchText: this.mediaSearchText.trim() || undefined,
      });
      if (this.collectionId !== collectionId) return;

      this.collectionToEdit = freshDetail.collection;
      if (this.detail?.collection.id === collectionId) {
        this.detail = { ...this.detail, collection: freshDetail.collection };
      }
    } catch {
      if (this.collectionId !== collectionId) return;
      this.collectionToEdit = currentCollection;
    } finally {
      this.openingCollectionModal = false;
    }

    if (this.collectionId === collectionId && this.collectionToEdit) {
      this.showCollectionModal = true;
    }
  }

  public onCollectionModalSaved(collection: Collection): void {
    this.showCollectionModal = false;
    this.collectionToEdit = null;
    if (this.detail?.collection.id === collection.id) {
      // The cover may also be a hidden gallery item, so refresh media and counts as well as
      // the collection metadata when the modal changes the cover settings.
      this.detail = { ...this.detail, collection };
      void this._reload(true);
      return;
    }
    // A newly created collection is not in the current listing yet, so refresh it.
    void this._reload(true);
  }

  public onCollectionModalClosed(): void {
    this.showCollectionModal = false;
    this.collectionToEdit = null;
  }

  // ── Organize (tree view / drag-and-drop reparenting) ──────────────────────

  public openTreeModal(): void {
    this.showTreeModal = true;
  }

  public onTreeModalClosed(): void {
    this.showTreeModal = false;
  }

  public onCollectionsMoved(): void {
    // A move may have affected the collection/children currently shown (e.g. a child was
    // moved out, or a new child was moved in) — reload to reflect it.
    void this._reload(true);
  }

  public async deleteCurrentCollection(): Promise<void> {
    if (!this.currentCollection) return;
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('collections.delete.confirm', { title: this.currentCollection.title }),
      this.i18n.tr('collections.delete.confirmTitle'),
    );
    if (!confirmed) return;
    try {
      await deleteCollection(this.currentCollection.id);
      // Navigate to parent if present, else back to root.
      const parent = this.breadcrumb.length > 0 ? this.breadcrumb[this.breadcrumb.length - 1] : null;
      if (parent) await this.router.load(`collections/${parent.id}`);
      else await this.router.load('collections');
    } catch (e) {
      await ConfirmDialog.alert(e instanceof Error ? e.message : String(e), this.i18n.tr('common.error'));
    }
  }

  // ── Picker (add/move-to-collection) ───────────────────────────────────────

  public openPickerForSelection(): void {
    if (!this.canAddSelectionToCollection || this.movingSelection) return;
    this._pickerContext = 'selection';
    this.pickerMode = 'add';
    this.showPicker = true;
  }

  public openMovePickerForSelection(): void {
    if (!this.canMoveSelection || this.movingSelection) return;
    this._pickerContext = 'moveSelection';
    this.pickerMode = 'move';
    this.showPicker = true;
  }

  public async onPickerSelected(collection: CollectionRef): Promise<void> {
    const context = this._pickerContext;
    this.showPicker = false;
    this._pickerContext = null;
    this._pickerLightboxItemId = null;
    if (context === 'selection') {
      const ids = this.selectedMediaIds;
      if (ids.length === 0) return;
      try {
        await addMediaItemsToCollection(collection.id, ids);
        this.clearSelection();
        this.selectMode = false;
      } catch (e) {
        await ConfirmDialog.alert(e instanceof Error ? e.message : String(e), this.i18n.tr('common.error'));
      }
    }
    if (context === 'moveSelection')
      await this.moveSelectedToCollection(collection);
  }

  public onPickerClosed(): void {
    this.showPicker = false;
    this._pickerContext = null;
    this._pickerLightboxItemId = null;
    this.pickerMode = 'add';
  }

  private async moveSelectedToCollection(target: CollectionRef): Promise<void> {
    const source = this.currentCollection;
    const ids = this.selectedMediaIds;
    if (!source || ids.length === 0 || source.id === target.id) return;

    this.movingSelection = true;
    try {
      await moveMediaItemsBetweenCollections(source.id, target.id, ids);
      await this._reload(true);
      this.selectMode = false;
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('collections.selection.moveToCollectionFailed'),
      );
    } finally {
      this.movingSelection = false;
    }
  }

  // ── Remove-from-collection (selection) ────────────────────────────────────

  public async removeSelectedFromCollection(): Promise<void> {
    if (!this.currentCollection || !this.hasSelection) return;
    const mediaIds = this.selectedMediaIds;
    const collectionIds = this.selectedCollectionItems.map(collection => collection.id);
    const count = mediaIds.length + collectionIds.length;
    const message = count === 1
      ? this.i18n.tr('collections.selection.removeFromCollectionOne')
      : this.i18n.tr('collections.selection.removeFromCollectionMany', { count });
    const confirmed = await ConfirmDialog.confirm(
      message,
      this.i18n.tr('collections.selection.removeFromCollectionTitle'),
    );
    if (!confirmed) return;
    try {
      if (mediaIds.length > 0)
        await removeMediaItemsFromCollection(this.currentCollection.id, mediaIds);
      for (const collectionId of collectionIds)
        await moveCollection(collectionId, null);
      await this._reload(true);
      this.clearSelection();
      this.selectMode = false;
    } catch (e) {
      await ConfirmDialog.alert(
        e instanceof Error ? e.message : String(e),
        this.i18n.tr('collections.selection.removeFromCollectionFailed'),
      );
    }
  }

  public async setSelectedVisibility(visibility: MediaVisibility): Promise<void> {
    if (!this.hasSelection || this.settingVisibility || this.isSelectingAll) return;

    const count = this.selectionCount;
    const message = count === 1
      ? this.i18n.tr('gallery.selection.setVisibilityConfirmOne', {
        visibility: this.i18n.tr(`media.visibility.${visibility}`),
      })
      : this.i18n.tr('gallery.selection.setVisibilityConfirmMany', {
        count,
        visibility: this.i18n.tr(`media.visibility.${visibility}`),
      });
    const confirmed = await ConfirmDialog.confirm(
      message,
      this.i18n.tr('gallery.selection.setVisibilityTitle'),
    );
    if (!confirmed) return;
    this.settingVisibility = true;
    try {
      const updatedMedia = new Map<string, MediaItem>();
      for (const id of this.selectedMediaIds) {
        const item = this.mediaItems.find(mediaItem => mediaItem.id === id);
        if (!item) continue;
        updatedMedia.set(id, await updateMediaItem(id, {
          title: item.title,
          description: item.description,
          capturedAt: item.capturedAt,
          tagNames: item.tags.map(tag => tag.name),
          isCapturedAtTimeAccurate: item.isCapturedAtTimeAccurate,
          visibility,
        }));
      }
      const updatedCollections = new Map<string, Collection>();
      for (const collection of this.selectedCollectionItems) {
        updatedCollections.set(collection.id, await updateCollection(collection.id, {
          title: collection.title,
          subtitle: collection.subtitle,
          collectionDate: collection.collectionDate,
          collectionDateTo: collection.collectionDateTo,
          visibility,
        }));
      }
      if (this.detail) {
        this.detail = {
          ...this.detail,
          childCollections: this.detail.childCollections.map(collection =>
            updatedCollections.get(collection.id) ?? collection),
          mediaItems: this.detail.mediaItems.map(item => updatedMedia.get(item.id) ?? item),
        };
      } else {
        this.rootCollections = this.rootCollections.map(collection =>
          updatedCollections.get(collection.id) ?? collection);
      }
      this.clearSelection();
      this.selectMode = false;
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : this.i18n.tr('gallery.selection.setVisibilityFailed'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.settingVisibility = false;
    }
  }

  // ── Drag-and-drop upload (only when viewing a collection) ─────────────────

  public onDragEnter(event: DragEvent): void {
    if (this.isRootView) return;
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.dragDepth++;
    this.dragOverlayVisible = true;
  }

  public onDragLeave(event: DragEvent): void {
    if (this.isRootView) return;
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.dragOverlayVisible = false;
  }

  public onDragOver(event: DragEvent): void {
    if (this.isRootView) return;
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  public async onDrop(event: DragEvent): Promise<void> {
    if (this.isRootView || !this.collectionId) return;
    event.preventDefault();
    this.dragDepth = 0;
    this.dragOverlayVisible = false;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    if (files.length === 0) return;

    const batchItems: UploadProgressItem[] = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      fileName: file.name,
      size: file.size,
      uploadedBytes: 0,
      status: 'queued',
      message: '',
    }));
    this.uploadItems = batchItems;
    this.uploadStartedAt = Date.now();
    this.uploadModalVisible = true;

    let protocolNumber: number | undefined;
    try {
      const protocol = await createImportProtocol('web');
      protocolNumber = protocol.number;
    } catch (error) {
      console.error('Failed to create import protocol for upload batch.', error);
    }

    const uploadedIds: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const progressItem = batchItems[i];
      try {
        progressItem.status = 'uploading';
        this.uploadItems = [...this.uploadItems];
        const uploaded = await uploadMediaFile(file, protocolNumber, (_pct, loaded) => {
          progressItem.uploadedBytes = loaded;
          this.uploadItems = [...this.uploadItems];
        });
        progressItem.status = uploaded.isDuplicate ? 'duplicate' : 'done';
        progressItem.uploadedBytes = progressItem.size;
        progressItem.message = uploaded.isDuplicate
          ? this.i18n.tr('gallery.upload.duplicateItem')
          : uploaded.title;
        uploadedIds.push(uploaded.id);
        this.uploadItems = [...this.uploadItems];
      } catch (error) {
        progressItem.status = 'error';
        progressItem.message = error instanceof Error ? error.message : String(error);
        this.uploadItems = [...this.uploadItems];
      }
    }

    if (protocolNumber != null) {
      try { await completeImportProtocol(protocolNumber); }
      catch (error) { console.error(`Failed to complete import protocol ${protocolNumber}.`, error); }
    }

    // Associate all successfully uploaded items with the currently viewed collection
    // in a single batched call so this is one network round-trip regardless of batch size.
    if (uploadedIds.length > 0 && this.collectionId) {
      try {
        await addMediaItemsToCollection(this.collectionId, uploadedIds);
      } catch (e) {
        console.error('Failed to associate uploaded items with collection.', e);
      }
    }

    await this._reload(true);
  }

  public dismissUploadModal(): void {
    this.uploadModalVisible = false;
    this.uploadItems = [];
    this.uploadStartedAt = null;
  }
}
